import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { CalendarClock, MapPin, Printer, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { fmtDateTime, fmtDay, fmtINR } from "../../lib/api";
import { PStatus, VehicleTypeBadge, fmtMinutes, modeLabel } from "./common";

/* Phase 201 — QR tickets the marshal scans at the gate: booking ticket + parking pass. */

export const QrBlock = ({ value, size = 168, caption, testId = "ticket-qr" }) => (
  <div className="flex flex-col items-center gap-2" data-testid={testId}>
    <div className="rounded-xl border bg-white p-3 shadow-sm">
      <QRCodeSVG value={value || "-"} size={size} level="M" includeMargin={false} />
    </div>
    {caption && <p className="font-mono text-sm tracking-[0.2em] font-semibold" data-testid={`${testId}-token`}>{caption}</p>}
  </div>
);

const Row = ({ label, children, testId }) => (
  <div className="flex items-start justify-between gap-3 text-sm"><span className="text-muted-foreground shrink-0">{label}</span><span className="text-right font-medium" data-testid={testId}>{children}</span></div>
);

export function BookingTicket({ booking: b, compact = false }) {
  if (!b) return null;
  return (
    <div className="space-y-4" data-testid="booking-ticket">
      <QrBlock value={b.qr_token} caption={b.qr_token} size={compact ? 140 : 176} />
      <div className="rounded-xl border bg-card p-4 space-y-2">
        <Row label="Booking" testId="ticket-booking-no"><span className="font-mono">{b.booking_no}</span></Row>
        <Row label="Status" testId="ticket-status"><PStatus status={b.status} /></Row>
        <Row label="Location"><span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-primary" /> {b.location_name}</span></Row>
        <Row label="Vehicle"><span className="inline-flex items-center gap-1.5"><VehicleTypeBadge type={b.vehicle_type} short /> <span className="font-mono">{b.vehicle_number}</span></span></Row>
        <Row label="From">{fmtDateTime(b.start_at)}</Row>
        <Row label="To">{fmtDateTime(b.end_at)}</Row>
        <Row label="Duration">{fmtMinutes(b.duration_minutes)}</Row>
        <Row label="Amount" testId="ticket-amount">{fmtINR(b.amount)} <PStatus status={b.payment_status} className="ml-1" /></Row>
        {b.last_receipt_no && <Row label="Receipt"><span className="font-mono text-xs">{b.last_receipt_no}</span></Row>}
      </div>
      <p className="text-xs text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>Show this QR (or tell the booking number / vehicle number) to the parking marshal at entry and exit.</span></p>
    </div>
  );
}

export function PassTicket({ pass: p }) {
  if (!p) return null;
  return (
    <div className="space-y-4" data-testid="pass-ticket">
      <QrBlock value={p.qr_token} caption={p.qr_token} />
      <div className="rounded-xl border bg-card p-4 space-y-2">
        <Row label="Pass" testId="ticket-pass-no"><span className="font-mono">{p.pass_no}</span></Row>
        <Row label="Type"><span className="capitalize">{p.pass_type}</span></Row>
        <Row label="Status"><PStatus status={p.status} /></Row>
        <Row label="Vehicle"><span className="inline-flex items-center gap-1.5"><VehicleTypeBadge type={p.vehicle_type} short /> <span className="font-mono">{p.vehicle_number}</span></span></Row>
        <Row label="Valid at">{p.location_name || "All locations"}</Row>
        <Row label="Valid"><span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-primary" /> {fmtDay(p.valid_from)} – {fmtDay(p.valid_to)}</span></Row>
        <Row label="Amount">{fmtINR(p.amount)} <PStatus status={p.payment_status} className="ml-1" /></Row>
        {p.receipt_no && <Row label="Receipt"><span className="font-mono text-xs">{p.receipt_no}</span></Row>}
      </div>
      <p className="text-xs text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>Show this QR at the gate — the marshal records your entry without any payment.</span></p>
    </div>
  );
}

export function TicketDialog({ open, onClose, booking, pass, title, description }) {
  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title || (pass ? "Parking pass" : "Parking ticket")}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {pass ? <PassTicket pass={pass} /> : <BookingTicket booking={booking} />}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => window.print()} data-testid="ticket-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
          <Button onClick={onClose} data-testid="ticket-done">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReceiptDialog({ receipt: r, onClose }) {
  return (
    <Dialog open={!!r} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="parking-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Receipt {r?.receipt_no}</DialogTitle>
          <DialogDescription>{r?.municipality?.name || ""} · Smart Parking</DialogDescription>
        </DialogHeader>
        {r && (
          <div className="rounded-xl border bg-card p-4 space-y-2">
            <Row label="Paid on">{fmtDateTime(r.paid_at)}</Row>
            <Row label="For"><span className="capitalize">{String(r.kind || "").replace(/_/g, " ")}</span>{r.ref_no ? <span className="font-mono text-xs ml-1">{r.ref_no}</span> : null}</Row>
            {r.location_name && <Row label="Location">{r.location_name}</Row>}
            {r.vehicle_number && <Row label="Vehicle"><span className="font-mono">{r.vehicle_number}</span></Row>}
            <Row label="Mode">{modeLabel(r.mode)}{r.collected_by_name ? ` · ${r.collected_by_name}` : ""}</Row>
            <Row label="Payer">{r.payer_name || "—"}{r.payer_mobile ? ` · ${r.payer_mobile}` : ""}</Row>
            <div className="border-t pt-2 flex items-center justify-between"><span className="text-muted-foreground text-sm">Amount paid</span><span className="text-xl font-semibold tabular-nums" data-testid="receipt-amount">{fmtINR(r.amount)}</span></div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => window.print()} data-testid="receipt-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
          <Button onClick={onClose} data-testid="receipt-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
